import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { serverApiUrl } from "../serverApi";
import { fmtDuration } from "../utils/formatters";
import * as W from "./accessWords";

// Master-portal login gate and session context.
//
// The portal used to be protected only by NABX_ADMIN_TOKEN, which the
// reverse-proxy injects into every request it forwards — so reaching the page
// was the same as being authorised. This wraps the front end in a real
// session: notabox-server sets an HttpOnly cookie that this component never
// touches, and every existing panel fetch carries it automatically because
// they are all same-origin.
//
// Mounted at the root (index.js), so it gates every surface — the landing
// chooser and Ada as well as the Console — rather than only the dashboard.
//
// The session it exposes also drives role-gated UI. A viewer sees the fleet
// but no write controls; a tenant admin sees Access Control; only a super
// admin sees other domains. The server enforces all of this independently —
// hiding a button is courtesy, not security.
//
// Besides signing in, a visitor can do for themselves what an email proves:
// choose a new password, accept an invitation, and set up a new box with the
// code that came with it (server/pkg/portalauth/onboarding.go). The server
// says in /auth/status which of these it offers. The on-device console has its
// own gate (LoginGate) and offers none of them.

const SessionContext = createContext(null);

// useSession returns the current portal session. Outside a PortalGate (the
// on-device single-edge console) it returns a permissive stub, because that
// build has its own separate login and no notion of roles.
export function useSession() {
  return (
    useContext(SessionContext) || {
      user: null,
      role: "super_admin",
      domains: [],
      isSuperAdmin: true,
      isTenantAdmin: true,
      canWrite: true,
      sendsEmail: false,
      managed: false,
      logout: () => {},
      refresh: () => {},
    }
  );
}

const rank = (role) => W.ROLES.findIndex((r) => r.key === role) + 1;

// Mirrors portalauth.Role.MinPasswordLen — the server is the authority; this
// only spares the user a round-trip to be told.
export const minPassword = (role) => (role === "super_admin" ? 12 : 10);

// newPasswordError says what is wrong with a new password and its
// confirmation, "" for nothing.
const newPasswordError = (password, confirm, need) =>
  password.length < need ? W.tooShort(need) : password !== confirm ? W.MISMATCH : "";

// typedCode is an emailed code as typed or pasted: its six digits.
export const typedCode = (v) => v.replace(/\D/g, "").slice(0, 6);

// askServer makes one call to notabox-server and answers {ok, status, data}.
// A refusal's data.error is a sentence fit to show the person, and so is the
// one put there when the server could not be reached at all.
export async function askServer(path, body) {
  try {
    const res = await fetch(serverApiUrl(path), {
      method: body ? "POST" : "GET",
      credentials: "same-origin",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    return { ok: res.ok, status: res.status, data: await res.json().catch(() => ({})) };
  } catch {
    return { ok: false, status: 0, data: { error: W.UNREACHABLE } };
  }
}

// The links in the server's emails open the console at /#reset=TOKEN or
// /#invite=TOKEN, in the fragment so the token never reaches a server or an
// access log. They are read before anything else, signed in or not, so a link
// opened in a browser holding another session still does what it says.
const LINKS = ["reset", "invite"];

function readEmailLink() {
  const h = new URLSearchParams(window.location.hash.slice(1));
  const kind = LINKS.find((k) => h.get(k));
  return kind ? { kind, token: h.get(kind) } : null;
}

export default function PortalGate({ children }) {
  // checking | setup | login | password | ready | error
  const [state, setState] = useState({ phase: "checking" });
  const [user, setUser] = useState(null);
  const [setupCodeRequired, setSetupCodeRequired] = useState(true);
  // What the server lets a visitor do for themselves: email (a forgotten
  // password, invitations) and signing up with a box's code.
  const [offers, setOffers] = useState({ email: false, signup: false });
  // A screen besides signing in: a forgotten password, a sign-up, or the
  // reset or invitation link an email carried. It shows whatever the phase.
  const [flow, setFlow] = useState(readEmailLink);
  // The password just used to sign in, held only long enough to prefill the
  // forced-change form. An onboarding password is issued to be pasted once —
  // making the operator re-type it to get rid of it is friction for nothing.
  const [usedPassword, setUsedPassword] = useState("");

  // The token leaves the address bar once read, path and query kept, so it is
  // neither left in the history nor handed on with a copied address. A link
  // opened in a tab already on the console changes only the fragment, which
  // loads nothing, so it is caught here too.
  useEffect(() => {
    const take = () => {
      const link = readEmailLink();
      if (!link) return;
      setFlow(link);
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    };
    take();
    window.addEventListener("hashchange", take);
    return () => window.removeEventListener("hashchange", take);
  }, []);

  const check = useCallback(async () => {
    try {
      const res = await fetch(serverApiUrl("/auth/status"), {
        credentials: "same-origin",
      });
      if (!res.ok) {
        setState({ phase: "login" });
        return;
      }
      const data = await res.json();
      setSetupCodeRequired(data.setup_code_required !== false);
      setOffers({ email: data.email === true, signup: data.signup === true });
      // Claim state is checked BEFORE authentication. An unclaimed portal
      // still honours the shared admin token, and the reverse-proxy attaches
      // that token to every request the browser makes — so trusting
      // `authenticated` first would render the dashboard and hide the claim
      // screen, with no way to ever create the first account.
      if (data.setup_required || data.configured === false) {
        setUser(null);
        setState({ phase: "setup" });
      } else if (data.authenticated) {
        setUser(data.user || null);
        // An account still holding the password an admin issued gets exactly
        // one screen. The server refuses everything else regardless.
        setState({
          phase: data.user?.must_change_password ? "password" : "ready",
        });
      } else {
        setUser(null);
        setState({ phase: "login" });
      }
    } catch {
      setState({ phase: "error", message: W.UNREACHABLE });
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  // Every way in ends here: signing in, a reset, an accepted invitation and a
  // sign-up all answer with the account and a session cookie.
  const onAuthed = useCallback((u, password) => {
    setFlow(null);
    setUser(u || null);
    if (u?.must_change_password) {
      setUsedPassword(password || "");
      setState({ phase: "password" });
      return;
    }
    setState({ phase: "ready" });
  }, []);

  const onPasswordChanged = useCallback(() => {
    setUsedPassword("");
    check();
  }, [check]);

  const logout = useCallback(async () => {
    try {
      await fetch(serverApiUrl("/auth/logout"), {
        method: "POST",
        credentials: "same-origin",
      });
    } catch {
      // Even if the call fails the local session is over; the cookie is
      // HttpOnly so the only honest thing to do is send them back to login.
    }
    setUser(null);
    // Re-derive the screen rather than assuming "login". On an unclaimed
    // portal there is no account to log in to, and hardcoding the login form
    // strands the operator on a screen no credential can satisfy.
    check();
  }, [check]);

  // A session can end server-side while the tab sits open — expiry, an admin
  // disabling the account, a server restart. Re-check on focus so the user
  // meets the login screen rather than a wall of failing panels.
  useEffect(() => {
    if (state.phase !== "ready") return undefined;
    const onFocus = () => {
      fetch(serverApiUrl("/auth/status"), { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && !d.authenticated) {
            setUser(null);
            setState({ phase: "login" });
          } else if (d && d.user) {
            setUser(d.user);
            if (d.user.must_change_password) {
              setState({ phase: "password" });
            }
          }
        })
        .catch(() => {});
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [state.phase]);

  const session = useMemo(
    () => ({
      user,
      role: user?.role || null,
      domains: user?.domains || [],
      isSuperAdmin: rank(user?.role) >= rank("super_admin"),
      isTenantAdmin: rank(user?.role) >= rank("tenant_admin"),
      canWrite: rank(user?.role) >= rank("operator"),
      // Whether the server can send account email: resets, invitations and a
      // person's own address all need it.
      sendsEmail: offers.email,
      managed: true,
      logout,
      refresh: check,
    }),
    [user, offers.email, logout, check],
  );

  if (flow) {
    const back = {
      onBack: () => setFlow(null),
      backLabel: state.phase === "ready" ? W.BACK_TO_CONSOLE : W.BACK_TO_SIGN_IN,
    };
    switch (flow.kind) {
      case "forgot":
        return <ForgotScreen login={flow.login} {...back} />;
      case "reset":
        return (
          <ResetScreen
            key={flow.token}
            token={flow.token}
            onAuthed={onAuthed}
            onForgot={() => setFlow({ kind: "forgot" })}
            {...back}
          />
        );
      case "invite":
        return <InviteScreen key={flow.token} token={flow.token} onAuthed={onAuthed} {...back} />;
      default:
        return <SignUpScreen onAuthed={onAuthed} {...back} />;
    }
  }

  if (state.phase === "ready") {
    return (
      <SessionContext.Provider value={session}>
        {children}
      </SessionContext.Provider>
    );
  }

  if (state.phase === "password") {
    return (
      <PasswordChangeScreen
        user={user}
        initialCurrent={usedPassword}
        onDone={onPasswordChanged}
        onSignOut={logout}
      />
    );
  }

  return (
    <AuthScreen
      phase={state.phase}
      message={state.message}
      setupCodeRequired={setupCodeRequired}
      offers={offers}
      onAuthed={onAuthed}
      onRetry={check}
      onForgot={(login) => setFlow({ kind: "forgot", login })}
      onSignUp={() => setFlow({ kind: "signup" })}
    />
  );
}

// ── Screen pieces ───────────────────────────────────────────────────────────

// Screen is the card every portal screen sits on.
function Screen({ title, lead, children }) {
  return (
    <div style={S.page}>
      <style>{css}</style>
      <div style={S.card}>
        <div style={S.brandRow}>
          <div style={S.logoDot} />
          <span style={S.brand}>Weavian</span>
        </div>
        <h1 style={S.title}>{title}</h1>
        <p style={S.subtitle}>{lead}</p>
        {children}
        <div style={S.footer}>Weavian · fleet control plane</div>
      </div>
    </div>
  );
}

// Field is one labelled input on a portal screen.
function Field({ id, label, hint, inputRef, style, ...input }) {
  return (
    <>
      <label style={S.label} htmlFor={id}>
        {label}
      </label>
      <input id={id} ref={inputRef} style={{ ...S.input, ...style }} {...input} />
      {hint && <div style={S.hint}>{hint}</div>}
    </>
  );
}

// NewPasswordFields asks for a new password twice.
function NewPasswordFields({ id, label, need, password, confirm, setPassword, setConfirm, autoFocus }) {
  return (
    <>
      <Field
        id={`${id}-pass`}
        label={label}
        type="password"
        autoComplete="new-password"
        autoFocus={autoFocus}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={W.atLeast(need)}
      />
      <Field
        id={`${id}-confirm`}
        label={`Confirm ${label.toLowerCase()}`}
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder={W.REENTER}
      />
    </>
  );
}

// UsernameField is where a new account chooses its name.
function UsernameField({ id, value, onChange, autoFocus }) {
  return (
    <Field
      id={id}
      label="Username"
      autoComplete="username"
      autoCapitalize="none"
      spellCheck="false"
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Choose a username"
      hint={W.USERNAME_HINT}
    />
  );
}

const Problem = ({ text }) =>
  text ? (
    <div style={S.error} role="alert">
      {text}
    </div>
  ) : null;

// ── Login / first-run screen ────────────────────────────────────────────────

function AuthScreen({
  phase,
  message,
  setupCodeRequired,
  offers,
  onAuthed,
  onRetry,
  onForgot,
  onSignUp,
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const userRef = useRef(null);

  const isSetup = phase === "setup";

  useEffect(() => {
    if ((phase === "login" || phase === "setup") && userRef.current) {
      userRef.current.focus();
    }
  }, [phase]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const wrong = isSetup
      ? newPasswordError(password, confirm, minPassword("super_admin"))
      : "";
    if (wrong) {
      setError(wrong);
      return;
    }

    setSubmitting(true);
    setError("");
    const r = await askServer(
      isSetup ? "/auth/setup" : "/auth/login",
      isSetup
        ? { username, password, setup_code: setupCode }
        : { username, password },
    );
    setSubmitting(false);
    if (r.ok) {
      setPassword("");
      setConfirm("");
      setSetupCode("");
      onAuthed(r.data.user, password);
    } else if (r.status === 429) {
      setError(
        r.data.retry_after
          ? `Too many failed attempts. Try again in ${fmtDuration(r.data.retry_after)}.`
          : "Too many failed attempts. Try again shortly.",
      );
    } else if (r.status === 409 && isSetup) {
      setError("This portal has already been claimed. Sign in instead.");
      onRetry();
    } else {
      setError(W.sentence(r.data.error) || `Sign-in failed (${r.status}).`);
    }
  };

  // Signing in by email, and the ways in that need email, are offered only
  // where the server says it can send it.
  const signingIn = phase === "login";
  const userLabel = signingIn && offers.email ? W.LOGIN_LABEL : "Username";

  return (
    <Screen
      title="Edge Console"
      lead={
        phase === "checking"
          ? "Connecting to the Weavian server…"
          : phase === "error"
            ? message
            : isSetup
              ? "First-time setup: create the super-admin account for this portal."
              : "Sign in to manage your fleet."
      }
    >
      {phase === "checking" ? (
        <div style={S.checking}>
          <span className="pg-spinner" />
          <span>Connecting…</span>
        </div>
      ) : phase === "error" ? (
        <button type="button" style={S.button} onClick={onRetry}>
          Retry
        </button>
      ) : (
        <form onSubmit={submit} style={S.form}>
          {isSetup && setupCodeRequired && (
            <Field
              id="pg-code"
              label="Setup code"
              type="text"
              autoComplete="off"
              spellCheck="false"
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.toUpperCase())}
              style={{ letterSpacing: 1.5 }}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              hint="Printed once in the notabox-server log at startup."
            />
          )}

          <Field
            id="pg-user"
            label={userLabel}
            inputRef={userRef}
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck="false"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={isSetup ? "Choose a username" : userLabel}
          />

          {isSetup ? (
            <NewPasswordFields
              id="pg"
              label="Password"
              need={minPassword("super_admin")}
              password={password}
              confirm={confirm}
              setPassword={setPassword}
              setConfirm={setConfirm}
            />
          ) : (
            <Field
              id="pg-pass"
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
            />
          )}

          <Problem text={error} />

          <button
            type="submit"
            style={S.button}
            disabled={submitting || !username || !password}
          >
            {submitting
              ? isSetup
                ? "Creating…"
                : "Signing in…"
              : isSetup
                ? "Create account & continue"
                : "Sign in"}
          </button>
          {signingIn && offers.email && (
            <button
              type="button"
              style={S.linkButton}
              onClick={() => onForgot(username.trim())}
            >
              {W.FORGOT_LINK}
            </button>
          )}
        </form>
      )}
      {signingIn && offers.signup && (
        <div style={S.aside}>
          {W.SIGNUP_PROMPT}{" "}
          <button type="button" style={S.link} onClick={onSignUp}>
            {W.SIGNUP_LINK}
          </button>
        </div>
      )}
    </Screen>
  );
}

// ── Forced password change ──────────────────────────────────────────────────
//
// An account is onboarded with a username and a password somebody else chose.
// This is the only screen that account can reach until it replaces it — the
// server enforces that in portalauth.Auth.Require; this is the way out.

function PasswordChangeScreen({ user, initialCurrent, onDone, onSignOut }) {
  const [current, setCurrent] = useState(initialCurrent || "");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const need = minPassword(user?.role);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const wrong =
      newPasswordError(next, confirm, need) ||
      (next === current ? "Choose a password different from the temporary one." : "");
    if (wrong) {
      setError(wrong);
      return;
    }

    setSubmitting(true);
    setError("");
    const r = await askServer("/auth/password", {
      current_password: current,
      new_password: next,
    });
    setSubmitting(false);
    if (r.ok) {
      setCurrent("");
      setNext("");
      setConfirm("");
      onDone();
      return;
    }
    setError(W.sentence(r.data.error) || `Could not set the password (${r.status}).`);
  };

  return (
    <Screen
      title="Choose your password"
      lead={
        <>
          <strong>{user?.username}</strong> is signed in with a temporary
          password. Set your own to continue.
        </>
      }
    >
      <form onSubmit={submit} style={S.form}>
        {/* Password managers attach the new credential to a username field;
            without one they offer to save nothing. */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={user?.username || ""}
          readOnly
          hidden
        />
        <Field
          id="pc-current"
          label="Temporary password"
          type="password"
          autoComplete="current-password"
          autoFocus={!initialCurrent}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="The password you were given"
        />
        <NewPasswordFields
          id="pc"
          label="New password"
          need={need}
          autoFocus={!!initialCurrent}
          password={next}
          confirm={confirm}
          setPassword={setNext}
          setConfirm={setConfirm}
        />

        <Problem text={error} />

        <button
          type="submit"
          style={S.button}
          disabled={submitting || !current || !next || !confirm}
        >
          {submitting ? "Saving…" : "Set password & continue"}
        </button>
        <button type="button" style={S.linkButton} onClick={onSignOut}>
          Sign out
        </button>
      </form>
    </Screen>
  );
}

// ── Forgotten password ──────────────────────────────────────────────────────
//
// The server answers the same whether the account exists or has an email, and
// sends the link after answering, so neither the answer nor how long it took
// says which accounts there are. The screen says only what that answer allows.

function ForgotScreen({ login: typed, onBack, backLabel }) {
  const [login, setLogin] = useState(typed || "");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await askServer("/auth/forgot", { login: login.trim() });
    setBusy(false);
    if (r.ok) setSent(true);
    else setError(W.refusal(r));
  };

  if (sent) {
    return (
      <Screen title={W.CHECK_EMAIL} lead={W.FORGOT_SENT}>
        <div style={S.form}>
          <div style={S.hint}>{W.FORGOT_NO_EMAIL}</div>
          <button type="button" style={S.button} onClick={onBack}>
            {backLabel}
          </button>
        </div>
      </Screen>
    );
  }
  return (
    <Screen title={W.FORGOT_TITLE} lead={W.FORGOT_LEAD}>
      <form onSubmit={submit} style={S.form}>
        <Field
          id="pf-login"
          label={W.LOGIN_LABEL}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck="false"
          autoFocus
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          placeholder={W.LOGIN_LABEL}
        />
        <Problem text={error} />
        <button type="submit" style={S.button} disabled={busy || !login.trim()}>
          {busy ? W.SENDING : W.FORGOT_SEND}
        </button>
        <button type="button" style={S.linkButton} onClick={onBack}>
          {backLabel}
        </button>
      </form>
    </Screen>
  );
}

// ── A new password from an emailed link ─────────────────────────────────────

function ResetScreen({ token, onAuthed, onForgot, onBack, backLabel }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  // Why the link no longer opens anything, once the server says so.
  const [ended, setEnded] = useState("");
  const [busy, setBusy] = useState(false);
  // The link does not say whose account it opens, so this is the least any
  // role needs; the server says when the account needs more.
  const need = minPassword("viewer");

  const submit = async (e) => {
    e.preventDefault();
    const wrong = newPasswordError(password, confirm, need);
    if (wrong) {
      setError(wrong);
      return;
    }
    setBusy(true);
    setError("");
    const r = await askServer("/auth/reset", { token, password });
    setBusy(false);
    if (r.ok) {
      onAuthed(r.data.user, password);
      return;
    }
    if (r.status === 410) setEnded(W.refusal(r));
    else setError(W.refusal(r));
  };

  if (ended) {
    return (
      <Screen title={W.RESET_ENDED_TITLE} lead={ended}>
        <div style={S.form}>
          <button type="button" style={S.button} onClick={onForgot}>
            {W.RESET_AGAIN}
          </button>
          <button type="button" style={S.linkButton} onClick={onBack}>
            {backLabel}
          </button>
        </div>
      </Screen>
    );
  }
  return (
    <Screen title={W.RESET_TITLE} lead={W.RESET_LEAD}>
      <form onSubmit={submit} style={S.form}>
        <NewPasswordFields
          id="pr"
          label="New password"
          need={need}
          autoFocus
          password={password}
          confirm={confirm}
          setPassword={setPassword}
          setConfirm={setConfirm}
        />
        <Problem text={error} />
        <button type="submit" style={S.button} disabled={busy || !password || !confirm}>
          {busy ? W.SAVING : W.RESET_SAVE}
        </button>
        <button type="button" style={S.linkButton} onClick={onBack}>
          {backLabel}
        </button>
      </form>
    </Screen>
  );
}

// ── An invitation from an emailed link ──────────────────────────────────────
//
// The address the invitation went to becomes the account's, so it is shown
// rather than asked for: opening the link is what proved it.

function InviteScreen({ token, onAuthed, onBack, backLabel }) {
  const [invite, setInvite] = useState(null);
  // {ended, text}: why the invitation could not be read or accepted.
  const [failed, setFailed] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const path = `/invite/${encodeURIComponent(token)}`;
  // The token has left the address bar, so a failed read is retried from
  // here, never by reloading the page.
  const load = useCallback(async () => {
    setFailed(null);
    const r = await askServer(path);
    if (r.ok) setInvite(r.data);
    else setFailed({ ended: r.status === 410, text: W.refusal(r) });
  }, [path]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const wrong = newPasswordError(password, confirm, minPassword(invite.role));
    if (wrong) {
      setError(wrong);
      return;
    }
    setBusy(true);
    setError("");
    const r = await askServer(path, { username: username.trim(), password });
    setBusy(false);
    if (r.ok) onAuthed(r.data.user, password);
    else if (r.status === 410) setFailed({ ended: true });
    else setError(W.refusal(r));
  };

  if (failed?.ended) {
    return (
      <Screen title={W.INVITE_ENDED_TITLE} lead={W.INVITE_ENDED}>
        <button type="button" style={S.button} onClick={onBack}>
          {backLabel}
        </button>
      </Screen>
    );
  }
  if (!invite) {
    return (
      <Screen title={W.YOUR_INVITATION} lead={failed ? failed.text : W.INVITE_OPENING}>
        <div style={S.form}>
          {failed && (
            <button type="button" style={S.button} onClick={load}>
              {W.TRY_AGAIN}
            </button>
          )}
          <button type="button" style={S.linkButton} onClick={onBack}>
            {backLabel}
          </button>
        </div>
      </Screen>
    );
  }
  const role = W.ROLES.find((r) => r.key === invite.role);
  return (
    <Screen title={W.inviteTitle(invite.organisation)} lead={W.inviteLead(invite)}>
      <form onSubmit={submit} style={S.form}>
        <dl style={S.facts}>
          <dt style={S.label}>{W.EMAIL}</dt>
          <dd style={S.factValue}>{invite.email}</dd>
          <dt style={S.label}>{W.ROLE}</dt>
          <dd style={S.factValue}>
            {role?.label || invite.role}
            {role && <div style={S.hint}>{role.blurb}</div>}
          </dd>
        </dl>
        <UsernameField id="pi-user" value={username} onChange={setUsername} autoFocus />
        <NewPasswordFields
          id="pi"
          label="Password"
          need={minPassword(invite.role)}
          password={password}
          confirm={confirm}
          setPassword={setPassword}
          setConfirm={setConfirm}
        />
        <Problem text={error} />
        <button
          type="submit"
          style={S.button}
          disabled={busy || !username.trim() || !password || !confirm}
        >
          {busy ? W.CREATING : W.INVITE_ACCEPT}
        </button>
        <button type="button" style={S.linkButton} onClick={onBack}>
          {backLabel}
        </button>
      </form>
    </Screen>
  );
}

// ── A new box ───────────────────────────────────────────────────────────────
//
// Whoever holds a box's code may create an organisation, become its first
// administrator and give it the box. The email is proved by a code typed back
// here, so a mistyped address cannot hand the organisation to whoever owns it.

function SignUpScreen({ onAuthed, onBack, backLabel }) {
  const [form, setForm] = useState({ code: "", organisation: "", email: "" });
  // {ticket, email} once a code has been emailed.
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // The first account of an organisation is its administrator.
  const need = minPassword("tenant_admin");

  const edit = (key) => (e) =>
    setForm({ ...form, [key]: key === "code" ? e.target.value.toUpperCase() : e.target.value });

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await askServer("/signup", {
      code: form.code.trim(),
      organisation: form.organisation.trim(),
      email: form.email.trim(),
    });
    setBusy(false);
    if (r.ok) setSent(r.data);
    else setError(W.refusal(r));
  };

  const finish = async (e) => {
    e.preventDefault();
    const wrong = newPasswordError(password, confirm, need);
    if (wrong) {
      setError(wrong);
      return;
    }
    setBusy(true);
    setError("");
    const r = await askServer("/signup/complete", {
      ticket: sent.ticket,
      code,
      username: username.trim(),
      password,
    });
    setBusy(false);
    if (r.ok) {
      onAuthed(r.data.user, password);
      return;
    }
    // An ended sign-up (out of time, too many wrong codes, or the box set up
    // by someone else meanwhile) starts again from the box's code.
    if (r.status === 410) {
      setSent(null);
      setCode("");
    }
    setError(W.refusal(r));
  };

  const back = () => {
    setSent(null);
    setError("");
  };

  if (!sent) {
    return (
      <Screen title={W.SIGNUP_TITLE} lead={W.SIGNUP_LEAD}>
        <form onSubmit={start} style={S.form}>
          <Field
            id="ps-box"
            label={W.BOX_CODE}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck="false"
            autoFocus
            value={form.code}
            onChange={edit("code")}
            style={{ letterSpacing: 1.5 }}
            placeholder={W.BOX_CODE_EXAMPLE}
            hint={W.BOX_CODE_WHERE}
          />
          <Field
            id="ps-org"
            label={W.ORG}
            autoComplete="organization"
            value={form.organisation}
            onChange={edit("organisation")}
            placeholder={W.ORG_EXAMPLE}
          />
          <Field
            id="ps-email"
            label={W.YOUR_EMAIL}
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={edit("email")}
            placeholder="you@company.com"
            hint={W.SIGNUP_EMAIL_HINT}
          />
          <Problem text={error} />
          <button
            type="submit"
            style={S.button}
            disabled={busy || !form.code.trim() || !form.organisation.trim() || !form.email.trim()}
          >
            {busy ? W.SENDING : W.SEND_CODE}
          </button>
          <button type="button" style={S.linkButton} onClick={onBack}>
            {backLabel}
          </button>
        </form>
      </Screen>
    );
  }
  return (
    <Screen title={W.SIGNUP_TITLE} lead={W.signupCodeSent(sent.email)}>
      <form onSubmit={finish} style={S.form}>
        <Field
          id="ps-code"
          label={W.CODE}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={(e) => setCode(typedCode(e.target.value))}
          style={{ letterSpacing: 4 }}
          placeholder="123456"
        />
        <UsernameField id="ps-user" value={username} onChange={setUsername} />
        <NewPasswordFields
          id="ps"
          label="Password"
          need={need}
          password={password}
          confirm={confirm}
          setPassword={setPassword}
          setConfirm={setConfirm}
        />
        <Problem text={error} />
        <button
          type="submit"
          style={S.button}
          disabled={busy || code.length < 6 || !username.trim() || !password || !confirm}
        >
          {busy ? W.CREATING : W.SIGNUP_FINISH}
        </button>
        <button type="button" style={S.linkButton} onClick={back}>
          {W.BACK}
        </button>
      </form>
    </Screen>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "radial-gradient(1200px 600px at 50% -10%, #16223a 0%, #0b1020 55%, #070a14 100%)",
    padding: 24,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 400,
    background: "rgba(17, 24, 39, 0.72)",
    border: "1px solid rgba(148, 163, 184, 0.16)",
    borderRadius: 18,
    padding: "34px 30px 22px",
    boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
    backdropFilter: "blur(14px)",
  },
  brandRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 22 },
  logoDot: {
    width: 14,
    height: 14,
    borderRadius: 5,
    background: "linear-gradient(135deg, #38bdf8, #6366f1)",
    boxShadow: "0 0 18px rgba(56,189,248,0.6)",
  },
  brand: { color: "#e2e8f0", fontWeight: 700, letterSpacing: 0.4, fontSize: 15 },
  title: { color: "#f8fafc", fontSize: 24, fontWeight: 700, margin: "0 0 6px" },
  subtitle: { color: "#94a3b8", fontSize: 14, margin: "0 0 22px" },
  form: { display: "flex", flexDirection: "column", gap: 8 },
  label: {
    color: "#cbd5e1",
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  input: {
    background: "rgba(15, 23, 42, 0.8)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    borderRadius: 10,
    padding: "12px 14px",
    color: "#f1f5f9",
    fontSize: 15,
    outline: "none",
  },
  hint: { color: "#94a3b8", fontSize: 12, lineHeight: 1.5, marginTop: -2 },
  error: {
    color: "#fca5a5",
    fontSize: 13,
    background: "rgba(239, 68, 68, 0.08)",
    border: "1px solid rgba(239, 68, 68, 0.25)",
    borderRadius: 8,
    padding: "8px 10px",
  },
  button: {
    marginTop: 8,
    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
  },
  linkButton: {
    marginTop: 4,
    background: "none",
    border: "none",
    color: "#94a3b8",
    fontSize: 13,
    cursor: "pointer",
    padding: 6,
  },
  link: {
    background: "none",
    border: "none",
    color: "#7dd3fc",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
  },
  aside: {
    marginTop: 18,
    paddingTop: 16,
    borderTop: "1px solid rgba(148, 163, 184, 0.14)",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 13,
  },
  facts: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: "8px 14px",
    alignItems: "baseline",
    margin: "0 0 8px",
    padding: "12px 14px",
    borderRadius: 10,
    background: "rgba(15, 23, 42, 0.55)",
    border: "1px solid rgba(148, 163, 184, 0.16)",
  },
  factValue: { margin: 0, color: "#f1f5f9", fontSize: 14, overflowWrap: "anywhere" },
  checking: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#94a3b8",
    fontSize: 14,
    padding: "12px 0",
  },
  footer: {
    marginTop: 22,
    textAlign: "center",
    color: "#475569",
    fontSize: 12,
  },
};

const css = `
.pg-spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(148,163,184,0.3);
  border-top-color: #38bdf8;
  display: inline-block;
  animation: pg-spin 0.8s linear infinite;
}
@keyframes pg-spin { to { transform: rotate(360deg); } }
`;
